import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

import { AuthService } from '../../../services/AuthService';
import { UserRole, ROLE_LABEL } from '../../../utils/permissions';

interface CreateUserDialogProps {
  open: boolean;
  onClose: () => void;
  /** 생성 성공 시 호출 — 목록 재조회 트리거. */
  onCreated: () => void;
}

interface FormState {
  name: string;
  username: string;
  email: string;
  password: string;
  role: UserRole;
}

const EMPTY: FormState = { name: '', username: '', email: '', password: '', role: 'viewer' };
const SELECTABLE_ROLES: UserRole[] = ['admin', 'editor', 'viewer'];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_USERNAME = 3;
const MIN_PASSWORD = 8;

/**
 * 신규 유저 생성 다이얼로그 — admin 전용.
 * 백엔드 POST /v0.1/auth/register 로 username/email/password/name/role 을 한 번에 등록한다.
 * username 은 로그인 아이디, email 은 연락처(알림 발송용) — 서로 별개 필드.
 */
const CreateUserDialog: React.FC<CreateUserDialogProps> = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY);
    setError(null);
    setSubmitting(false);
    setShowPassword(false);
  }, [open]);

  const handleSubmit = async () => {
    const name = form.name.trim();
    const username = form.username.trim();
    const email = form.email.trim();
    if (!name) {
      setError('이름을 입력해 주세요.');
      return;
    }
    if (username.length < MIN_USERNAME) {
      setError(`아이디는 최소 ${MIN_USERNAME}자 이상이어야 합니다.`);
      return;
    }
    if (!EMAIL_RE.test(email)) {
      setError('올바른 이메일 형식이 아닙니다.');
      return;
    }
    if (form.password.length < MIN_PASSWORD) {
      setError(`비밀번호는 최소 ${MIN_PASSWORD}자 이상이어야 합니다.`);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await AuthService.registerUser({ username, name, email, password: form.password, role: form.role });
      onCreated();
      onClose();
    } catch (e: any) {
      setError(e?.message || '유저 생성 실패');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !submitting && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>신규 유저 생성</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Stack spacing={2}>
          <TextField
            label="이름"
            required
            fullWidth
            autoFocus
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
          <TextField
            label="아이디"
            required
            fullWidth
            value={form.username}
            onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
            helperText="로그인에 사용할 아이디"
          />
          <TextField
            label="이메일"
            type="email"
            required
            fullWidth
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            placeholder="user@example.com"
          />
          <TextField
            label="비밀번호"
            type={showPassword ? 'text' : 'password'}
            required
            fullWidth
            value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            helperText={`최소 ${MIN_PASSWORD}자`}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 표시'}
                    onClick={() => setShowPassword((v) => !v)}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="권한"
            select
            required
            fullWidth
            value={form.role}
            onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as UserRole }))}
            helperText="관리자: 전체 관리 / 편집자: 데이터 수정 / 뷰어: 조회만"
          >
            {SELECTABLE_ROLES.map((r) => (
              <MenuItem key={r} value={r}>
                {ROLE_LABEL[r]}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          취소
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
          {submitting ? '생성 중...' : '생성'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateUserDialog;
